/**
 * Clean Console Bootstrap
 * Suppresses all noisy console logs, info, debug, and library warnings (such as Recharts container sizing,
 * unload permission policy, tracking prevention) across the entire frontend application.
 * Leaves the browser developer tools console 100% clean and professional.
 */

if (typeof window !== 'undefined') {
  const noop = () => {};

  // 1. Completely silence verbose console outputs across all pages
  console.log = noop;
  console.info = noop;
  console.debug = noop;

  // 2. Filter out library noise from console.warn & console.error
  const originalWarn = console.warn ? console.warn.bind(console) : noop;
  const originalError = console.error ? console.error.bind(console) : noop;

  const shouldSuppress = (args) => {
    try {
      const msg = args
        .map((a) => (typeof a === 'string' ? a : (a && a.message ? a.message : '')))
        .join(' ');
      if (!msg) return false;

      // Filter Recharts width/height sizing warnings
      if (msg.includes('The width') && msg.includes('of chart should be greater than 0')) {
        return true;
      }
      // Filter Permissions-Policy unload violation warnings
      if (msg.includes('Permissions policy violation') && msg.includes('unload')) {
        return true;
      }
      // Filter tracking prevention storage access messages
      if (msg.includes('Tracking Prevention blocked access to storage')) {
        return true;
      }
      // Filter React function component defaultProps warning
      if (msg.includes('Support for defaultProps will be removed from function components')) {
        return true;
      }
    } catch {
      return false;
    }
    return false;
  };

  console.warn = (...args) => {
    if (shouldSuppress(args)) return;
    originalWarn(...args);
  };

  console.error = (...args) => {
    if (shouldSuppress(args)) return;
    originalError(...args);
  };

  // 3. Suppress the "ResizeObserver loop completed with undelivered notifications"
  // browser warning. This fires whenever a ResizeObserver callback (ours in
  // UniversalTable/DatabankForm, or one used internally by @tanstack/react-virtual)
  // triggers another resize within the same frame — it's a benign, spec-documented
  // browser notice (https://github.com/WICG/resize-observer/blob/master/explainer.md),
  // not an app bug, but CRA's dev-time error overlay treats any uncaught window
  // 'error' event as fatal and blocks the whole page with a red screen for it.
  const isBenignResizeObserverError = (message) =>
    typeof message === 'string' &&
    (message === 'ResizeObserver loop completed with undelivered notifications.' ||
      message === 'ResizeObserver loop limit exceeded');

  window.addEventListener('error', (event) => {
    if (!isBenignResizeObserverError(event.message)) return;
    // Stop other listeners (e.g. the dev overlay's own handler) from treating this
    // as a fatal error, if ours runs first.
    event.stopImmediatePropagation();
    event.preventDefault();
    // If webpack-dev-server's overlay iframe already rendered for this error
    // (listener ordering means ours doesn't always run first), hide it directly.
    const overlay = document.getElementById('webpack-dev-server-client-overlay');
    const overlayDiv = document.getElementById('webpack-dev-server-client-overlay-div');
    if (overlay) overlay.style.display = 'none';
    if (overlayDiv) overlayDiv.style.display = 'none';
  });
}
